import { Container, Sprite, type DestroyOptions, type Texture } from "pixi.js";
import type { Log, PresentationTaskRunner, TimerSleep } from "@slot/presentation";
import { BASE_ROWS } from "../gridSize";
import { EDGE_FIRE_TUNING } from "../tuning/featureTuning";
import { EDGE_FIRE_LAYOUT, frameBox, handoffFrameUnits } from "./layout";

/**
 * The artist's grid edge fire (`edge_fire.riv`, played as its Spine conversion), one artboard per edge: its boolean `on`
 * ignites a piece at the middle of its edge, racing to the corners and along the sides (0.3 s), then it burns on a loop;
 * off, it dies out (0.4 s) and draws nothing.
 */
export const EDGE_FIRE_RIVE = { on: "on" } as const;

/** What the edge fire asks of each piece's art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface EdgeFireArt {
  readonly texture: Texture;
  region(x: number, y: number, width: number, height: number): Texture;
  resize(scale: number): void;
  setBoolean(name: string, value: boolean): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

interface Pieces {
  top: EdgeFireArt;
  bottom: EdgeFireArt;
  /** The top piece's flames over the frame's top edge (behind the pots), its edge and corners, its side columns. */
  topAbove: Sprite;
  topEdge: Sprite;
  topSides: readonly Sprite[];
  /** The bottom piece's edge and corners, and its side columns. */
  bottomEdge: Sprite;
  bottomSides: readonly Sprite[];
}

/**
 * GROW's fire on the grid's frame (the artist's handoff): as GROW's board impact reaches the frame, a piece on the frame's
 * top edge and one on its bottom edge ignite and burn while the grid grows, the bottom one riding the frame's bottom edge
 * down; then they die out. They burn again, on a loop, from the bonus's payout to its end (the user, 2026-10-15), whether
 * GROW played or not. This container is the layer over the frame (the grid puts it under the coins): the top piece
 * from the frame's top edge down, and the bottom piece. `behindPots` is the top piece's flames over the frame's top edge,
 * one render cut in two: the scene puts it under the pots and their heap of coins (the frame hides the heap's lower half,
 * so no layer could be both over the frame and under the heap). Drawn to the game's frame, which is wider than the one the
 * artist drew it around: each piece at its width, from its own corner. The artist's pieces cover the sides only to the
 * middle of the base frame's; on a grown frame each piece's side columns are drawn longer, their flames stretched along
 * the side, so the fire still runs all round it (the user, 2026-10-12). Drawn on the UI's runner while it burns and dies
 * out, at the tuning's frame rate: never work a round waits for. Nothing burns before the art is in.
 */
export class GridEdgeFire extends Container {
  /** The top piece's flames over the frame's top edge (the scene adds this layer under the pots). */
  public readonly behindPots: Container;
  private readonly ambientRunner: PresentationTaskRunner;
  private readonly log: Log;
  private pieces: Pieces | null = null;
  private lit = false;
  /** Engine seconds it is still drawn after going off (its dying out). */
  private fading = 0;
  private drawing = false;
  private ignited = 0;
  /** Its going out on its own (`goOutAfter`), until it does or it is lit or put out first. */
  private goingOut: TimerSleep | null = null;
  private rows: number = BASE_ROWS;
  /** Device pixels per design unit where the grid is drawn (the textures' size follows it). */
  private pixelsPerUnit = 1;

  public constructor(input: { ambientRunner: PresentationTaskRunner; log: Log }) {
    super({ label: "GridEdgeFire" });
    this.behindPots = new Container({ label: "GridEdgeFireAbove" });
    this.ambientRunner = input.ambientRunner;
    this.log = input.log;
  }

  /** The artist's two pieces, loaded: laid on the frame's corners, hidden until the fire is lit. */
  public setArt(arts: { top: EdgeFireArt; bottom: EdgeFireArt }): void {
    if (this.destroyed || this.pieces) {
      arts.top.dispose();
      arts.bottom.dispose();
      return;
    }
    const { artboard, top, bottom, side } = EDGE_FIRE_LAYOUT;
    const units = this.units;
    const sprite = (art: EdgeFireArt, label: string, x: number, y: number, width: number, height: number): Sprite => {
      const made = new Sprite({ texture: art.region(x, y, width, height), label });
      made.setSize(width * units, height * units);
      made.visible = false;
      return made;
    };
    const right = artboard.width - side.width;
    const topAbove = sprite(arts.top, "EdgeFireTopAbove", 0, 0, artboard.width, top.y);
    const topEdge = sprite(arts.top, "EdgeFireTop", 0, top.y, artboard.width, side.top - top.y);
    const topSides = [
      sprite(arts.top, "EdgeFireTopLeft", 0, side.top, side.width, artboard.height - side.top),
      sprite(arts.top, "EdgeFireTopRight", right, side.top, side.width, artboard.height - side.top),
    ];
    const bottomEdge = sprite(arts.bottom, "EdgeFireBottom", 0, side.bottom, artboard.width, artboard.height - side.bottom);
    const bottomSides = [sprite(arts.bottom, "EdgeFireBottomLeft", 0, 0, side.width, side.bottom), sprite(arts.bottom, "EdgeFireBottomRight", right, 0, side.width, side.bottom)];
    // Each piece's corner on the frame's: the top piece's flames over the edge above it, the rest from it down.
    const box = frameBox(BASE_ROWS);
    const topLeft = box.x - top.x * units;
    topAbove.position.set(topLeft, box.y - top.y * units);
    topEdge.position.set(topLeft, box.y);
    topSides[0]!.x = topLeft;
    topSides[1]!.x = topLeft + right * units;
    const bottomLeft = box.x - bottom.x * units;
    bottomEdge.x = bottomLeft;
    bottomSides[0]!.x = bottomLeft;
    bottomSides[1]!.x = bottomLeft + right * units;
    this.behindPots.addChild(topAbove);
    this.addChild(topEdge, ...topSides, bottomEdge, ...bottomSides);
    this.pieces = { top: arts.top, bottom: arts.bottom, topAbove, topEdge, topSides, bottomEdge, bottomSides };
    for (const art of [arts.top, arts.bottom]) art.resize(this.textureScale());
    this.follow(this.rows);
    this.log("[GridEdgeFire] the edge fire is in");
    if (this.lit) this.apply();
  }

  /** Whether the fire is in, burns, how many times it has ignited, and whether it is drawn (the dev hook's read). */
  public get state(): { loaded: boolean; on: boolean; ignited: number; drawn: boolean } {
    return { loaded: this.pieces !== null, on: this.lit, ignited: this.ignited, drawn: this.pieces?.topEdge.visible ?? false };
  }

  /** Device pixels per design unit where the grid is drawn: the textures follow it. */
  public setPixelsPerUnit(pixelsPerUnit: number): void {
    if (!(pixelsPerUnit > 0) || pixelsPerUnit === this.pixelsPerUnit) return;
    this.pixelsPerUnit = pixelsPerUnit;
    if (this.pieces) for (const art of [this.pieces.top, this.pieces.bottom]) art.resize(this.textureScale());
  }

  /**
   * The pieces on the frame for `rows` rows in play (fractional while the grid grows): the bottom piece on its bottom edge,
   * and each piece's side columns as much longer as half the frame has grown, so that their tips meet in the middle of its
   * sides as on the base frame.
   */
  public follow(rows: number): void {
    this.rows = rows;
    if (!this.pieces) return;
    const { artboard, top, bottom, side } = EDGE_FIRE_LAYOUT;
    const units = this.units;
    const box = frameBox(rows);
    const longer = (box.height - frameBox(BASE_ROWS).height) / 2;
    for (const column of this.pieces.topSides) {
      column.y = box.y + (side.top - top.y) * units;
      column.height = (artboard.height - side.top) * units + longer;
    }
    // The bottom piece's edge from its line down; its columns up from it.
    const line = box.y + box.height - (bottom.y - side.bottom) * units;
    this.pieces.bottomEdge.y = line;
    for (const column of this.pieces.bottomSides) {
      column.height = side.bottom * units + longer;
      column.y = line - column.height;
    }
  }

  /** The fire on (it ignites, then burns) or off (it dies out); the same again changes nothing. */
  public set(on: boolean): void {
    this.goingOut?.cancel();
    this.goingOut = null;
    if (this.lit === on) return;
    this.lit = on;
    if (on) this.ignited += 1;
    if (this.pieces && !this.destroyed) this.apply();
  }

  /** It burns on for `seconds` of the UI's time, then dies out, unless it is lit or put out before. */
  public goOutAfter(seconds: number): void {
    this.goingOut?.cancel();
    const timer = this.ambientRunner.countdown(seconds);
    this.goingOut = timer;
    void timer.done.then(() => {
      if (this.goingOut !== timer || this.destroyed) return;
      this.set(false);
    });
  }

  public override destroy(options?: DestroyOptions): void {
    this.goingOut?.cancel();
    this.goingOut = null;
    const pieces = this.pieces;
    this.pieces = null;
    if (pieces) {
      pieces.top.dispose();
      pieces.bottom.dispose();
    }
    if (!this.behindPots.destroyed) this.behindPots.destroy({ children: true });
    super.destroy(options);
  }

  /** Design units per artboard pixel: the artist's frame drawn to the game's frame's width. */
  private get units(): number {
    return handoffFrameUnits(EDGE_FIRE_LAYOUT.frameWidth);
  }

  /** Texture pixels per artboard pixel: its size on the screen, at the tuning's share. */
  private textureScale(): number {
    return this.units * this.pixelsPerUnit * EDGE_FIRE_TUNING.resolution;
  }

  /** Every sprite the pieces are drawn on. */
  private sprites(pieces: Pieces): Sprite[] {
    return [pieces.topAbove, pieces.topEdge, ...pieces.topSides, pieces.bottomEdge, ...pieces.bottomSides];
  }

  /** The pieces told the fire's state, shown and drawn: while on, and for its dying out after. */
  private apply(): void {
    const pieces = this.pieces!;
    for (const art of [pieces.top, pieces.bottom]) art.setBoolean(EDGE_FIRE_RIVE.on, this.lit);
    this.fading = this.lit ? 0 : EDGE_FIRE_TUNING.offSeconds;
    if (this.lit) for (const sprite of this.sprites(pieces)) sprite.visible = true;
    if (pieces.topEdge.visible) for (const art of [pieces.top, pieces.bottom]) art.render(0);
    this.draw();
  }

  /** Whether the fire is drawn: on, or dying out. */
  private get burning(): boolean {
    return this.pieces !== null && this.pieces.topEdge.visible && (this.lit || this.fading > 0);
  }

  /** Both pieces drawn at their own pace while the fire burns or dies out; hidden once it has died out. */
  private draw(): void {
    if (this.drawing) return;
    this.drawing = true;
    let last = 0;
    void this.ambientRunner
      .tickInScope(
        "",
        (elapsed) => {
          const delta = elapsed - last;
          last = elapsed;
          const pieces = this.pieces;
          if (!pieces || this.destroyed || !this.burning) return;
          pieces.top.advance(delta);
          pieces.bottom.advance(delta);
          if (this.lit) return;
          this.fading = Math.max(0, this.fading - delta);
          if (this.fading > 0) return;
          for (const sprite of this.sprites(pieces)) sprite.visible = false;
        },
        () => this.destroyed || !this.burning,
      )
      .then(() => {
        this.drawing = false;
        // Lit again as it died out.
        if (!this.destroyed && this.burning) this.draw();
      });
  }
}
